import {
  useGetSingleQuestionPropertiesByStatusAndLocationQuery,
  SingleQuestionPropertiesLocationType,
  type SingleQuestionPropertiesStatus,
  useSearchLearningObjectiveStatementsQuery,
  OrderType,
  useGetTagsByCategoryQuery,
  QuestionTagCategory,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Box, Loading } from '@shared/components';
import { useMemo } from 'react';

import { useQBankStructureDashBoardSearchParams } from '../../hooks';
import { QBankStructureDashBoardNavigationTabs } from '../qbank-structure-dashboard-navigation-tabs';
import { QBankStructureLosSingleQuestionDashboardTable } from '../qbank-structure-los-single-question-dashboard-table';

export default function QBankStructureLOSSingleQuestionDashboard() {
  const { qBankStructureInformation, qBankStructureLevelItems } = useQBankStructure();
  const [{ status }] = useQBankStructureDashBoardSearchParams();
  const { data, loading: isGetQuestionPropertiesLoading } =
    useGetSingleQuestionPropertiesByStatusAndLocationQuery({
      variables: {
        input: {
          status: status as SingleQuestionPropertiesStatus,
          qBankStructureId: qBankStructureInformation.id,
          locationType: SingleQuestionPropertiesLocationType.LearningObjectiveStatement,
        },
      },
    });

  const { data: getDifficultyTagsResult, loading: isDifficultyTagsLoading } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Difficulty } },
    });

  const { data: getCalculationTagsResult, loading: isCalculationTagsLoading } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Calculation } },
    });

  const hasLevelItem = useMemo(() => {
    return qBankStructureLevelItems.some((levelItem) => levelItem.qBankStructureLevel.level !== 1);
  }, [qBankStructureLevelItems]);

  const { data: searchLOSResult, loading: isLoadingLOS } =
    useSearchLearningObjectiveStatementsQuery({
      skip: !hasLevelItem,
      variables: {
        order: {
          field: 'updatedAt',
          type: OrderType.Asc,
        },
        paging: {
          currentPage: 0,
          pageSize: 9999, // NOTE: this is a workaround to get all LOS by estimation
        },
        search: {
          qBankStructureId: qBankStructureInformation.id,
        },
      },
    });

  const losData = useMemo(
    () => searchLOSResult?.qbsSearchLearningObjectiveStatements.learningObjectiveStatements ?? [],
    [searchLOSResult?.qbsSearchLearningObjectiveStatements.learningObjectiveStatements],
  );

  const singleQuestionProperties = useMemo(
    () => data?.qctGetSingleQuestionPropertiesByStatusAndLocation ?? [],
    [data?.qctGetSingleQuestionPropertiesByStatusAndLocation],
  );

  const difficultyTags = useMemo(
    () => getDifficultyTagsResult?.qbsGetTagsByCategory ?? [],
    [getDifficultyTagsResult?.qbsGetTagsByCategory],
  );

  const calculationTags = useMemo(
    () => getCalculationTagsResult?.qbsGetTagsByCategory ?? [],
    [getCalculationTagsResult?.qbsGetTagsByCategory],
  );

  const isLoading =
    isGetQuestionPropertiesLoading ||
    isLoadingLOS ||
    isDifficultyTagsLoading ||
    isCalculationTagsLoading;

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
        overflow: 'hidden',
      })}
    >
      <QBankStructureDashBoardNavigationTabs
        questionStatuses={['PUBLISHED', 'UNPUBLISHED', 'DISABLED']}
      />
      {isLoading ? (
        <Box sx={(theme) => ({ p: theme.spacing(6) })}>
          <Loading />
        </Box>
      ) : (
        <QBankStructureLosSingleQuestionDashboardTable
          data={singleQuestionProperties}
          los={losData}
          difficultyTags={difficultyTags}
          calculationTags={calculationTags}
        />
      )}
    </Box>
  );
}
